import { FC, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Assets } from '@assets';
import { Referral } from '@components';
import { Button } from '@elements/button';
import { Text } from '@elements/text';
import { i18n } from '@i18n';
import { closeModalSelector, useGlobalStore, useGuestStore, useModalStore } from '@store';
import { growsurfApi, sendAnalyticsEvent, testProps } from '@utils';

export interface ReferralModalProps {
  children?: React.ReactNode;
  discountPercentage: number;
  earnDollars: number;
}

export const ReferralModal: FC<ReferralModalProps> = ({ discountPercentage, earnDollars }) => {
  const guest = useGuestStore(state => state.guest);
  const [isOpen, setIsOpen] = useState(false);
  const setIsReferralModalClosed = useGlobalStore(state => state.setIsReferralModalClosed);
  const closeModal = useModalStore(closeModalSelector);
  const [isGrowsurfInitialized, setIsGrowsurfInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (window.growsurf) {
      setIsGrowsurfInitialized(true);
      return;
    }

    const onGrowsurfReady = () => {
      setIsGrowsurfInitialized(true);
    };

    window.addEventListener('grsfReady', onGrowsurfReady);

    const intervalId = setInterval(() => {
      if (window.growsurf) {
        setIsGrowsurfInitialized(true);
        clearInterval(intervalId);
        window.removeEventListener('grsfReady', onGrowsurfReady);
      }
    }, 300);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('grsfReady', onGrowsurfReady);
    };
  }, []);

  const handleButtonClick = async () => {
    setIsLoading(true);

    if (guest?.email) {
      await growsurfApi.addParticipant({ email: guest.email });
    }

    setIsLoading(false);
    sendAnalyticsEvent('Referral Click', {
      firstName: guest?.firstName ?? '',
      lastName: guest?.lastName ?? '',
      email: guest?.email ?? '',
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    sendAnalyticsEvent(isOpen ? 'Referral url modal close' : 'Referral modal close');
    closeModal();
    setIsReferralModalClosed(true);
  };

  return (
    <div>
      <div className="absolute top-0 right-0 pt-4 pr-4">
        <Button
          type="button"
          onClick={handleClose}
          variant="tertiary"
          icon="close"
          {...testProps('referral-modal-button-close')}
        />
      </div>
      <div className="flex flex-col items-center justify-center gap-4 px-4 pb-2 bg-mono-100 rounded-2xl">
        <Assets.Gift />
        <Text
          className="mt-2 text-center"
          variant="h2-36/sb"
          {...testProps('referral-modal-title')}>
          {i18n.t('checkout.confirmation.referral.modal.title', {
            earnDollars,
          })}
        </Text>
        <Text
          className="mt-2 text-center"
          variant="subTitle-20/r"
          {...testProps('referral-modal-description')}>
          {i18n.t('checkout.confirmation.referral.modal.description', {
            discountPercentage,
            earnDollars,
          })}
        </Text>
        <Referral firstName={guest?.firstName} lastName={guest?.lastName} isModalOpen={isOpen} />
        <div className={twMerge('w-full', isOpen ? 'hidden' : 'block')}>
          <Button
            isLoading={!isGrowsurfInitialized || isLoading}
            className="w-full"
            variant="primary"
            onClick={handleButtonClick}
            title={i18n.t('checkout.confirmation.referral.modal.button')}
            {...testProps('referral-modal-button-primary')}
          />
        </div>
      </div>
    </div>
  );
};
